// src/components/organizer/ParticipantManagementWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Button,
  TextField,
  InputAdornment,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as ApprovedIcon,
  Schedule as PendingIcon,
  Cancel as RejectedIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Download as ExportIcon
} from '@mui/icons-material';

const ParticipantManagementWidget = ({ data }) => {
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  if (!data) return null;

  const recentRegistrations = data.recentRegistrations || [
    { 
      id: 1, 
      name: 'Ana García', 
      email: 'ana@email.com', 
      event: 'Maratón Ciudad', 
      status: 'approved',
      registrationDate: '2024-12-09',
      category: 'Elite'
    },
    { 
      id: 2, 
      name: 'Carlos López', 
      email: 'carlos@email.com', 
      event: 'Copa Juvenil', 
      status: 'pending',
      registrationDate: '2024-12-09',
      category: 'Junior'
    },
    { 
      id: 3, 
      name: 'María Rodríguez', 
      email: 'maria@email.com', 
      event: 'Triatlón Regional', 
      status: 'approved',
      registrationDate: '2024-12-08',
      category: 'Open'
    }
  ];

  const pendingApprovals = recentRegistrations.filter(p => p.status === 'pending');
  const approvedParticipants = recentRegistrations.filter(p => p.status === 'approved');

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <ApprovedIcon color="success" />;
      case 'pending': return <PendingIcon color="warning" />;
      case 'rejected': return <RejectedIcon color="error" />;
      default: return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning'; 
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Aprobado';
      case 'pending': return 'Pendiente';
      case 'rejected': return 'Rechazado';
      default: return status;
    }
  };

  const filteredParticipants = (tabValue === 0 ? recentRegistrations : 
                               tabValue === 1 ? pendingApprovals : 
                               approvedParticipants)
    .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="👥 Gestión de Participantes"
        subheader={`${pendingApprovals.length} pendientes de aprobación`}
        action={
          <Button
            startIcon={<ExportIcon />}
            size="small"
            variant="outlined"
          >
            Exportar
          </Button>
        }
      />
      
      <CardContent>
        {/* Estadísticas rápidas */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Box textAlign="center" sx={{ p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="h6" color="success.dark">
                  {data.approved || approvedParticipants.length}
                </Typography>
                <Typography variant="caption" color="success.dark">
                  Aprobados
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center" sx={{ p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="h6" color="warning.dark">
                  {data.pending || pendingApprovals.length}
                </Typography>
                <Typography variant="caption" color="warning.dark">
                  Pendientes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center" sx={{ p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="h6" color="info.dark">
                  {data.total || recentRegistrations.length}
                </Typography>
                <Typography variant="caption" color="info.dark">
                  Total
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Buscador */}
        <TextField
          size="small"
          placeholder="Buscar participante..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, width: '100%' }}
        />

        {/* Tabs */}
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label={`Todos (${recentRegistrations.length})`} />
          <Tab label={`Pendientes (${pendingApprovals.length})`} />
          <Tab label={`Aprobados (${approvedParticipants.length})`} />
        </Tabs>

        {/* Lista de participantes */}
        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
          {filteredParticipants.map((participant) => (
            <ListItem 
              key={participant.id} 
              divider
              secondaryAction={
                <Box display="flex" gap={0.5}>
                  <IconButton size="small">
                    <EmailIcon fontSize="small" />
                  </IconButton>
                  {participant.status === 'pending' && (
                    <>
                      <IconButton size="small" color="success">
                        <ApprovedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <RejectedIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              }
            >
              <ListItemAvatar>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {participant.name[0]}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="bold">
                      {participant.name}
                    </Typography>
                    <Chip
                      label={getStatusLabel(participant.status)}
                      color={getStatusColor(participant.status)}
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 16 }}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {participant.event} • {participant.category}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(participant.registrationDate).toLocaleDateString('es-ES')}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default ParticipantManagementWidget;